/**
 * Semantic tones shared by Badge, StatusChip, Tag, Callout, ProgressBar, Toast and the logistics composites.
 *
 * - `neutral`     default, no meaning
 * - `brand`       Prussian identity (a label that belongs to Zagru One itself, not a state)
 * - `accent`      turquoise: active, selected, in-focus, recommended key action. **Never success.**
 * - `success`     done, available, healthy (a true green)
 * - `warning`     at risk, needs a look soon (amber)
 * - `delayed`     late against a plan (orange)
 * - `danger`      failed, exception, destructive — `critical` is the same family under its product name
 * - `blocked`     cannot proceed until something else happens (rose)
 * - `info`        informational, expected, received (azure)
 * - `processing`  in flight: allocated, picking, packing (blue)
 * - `ai`          produced or recommended by AI (violet)
 * - `attention`   needs a person, not an error (orchid)
 *
 * Colour is never the only signal: chips carry a dot or icon **and** a text label.
 */
export type Tone =
  | 'neutral'
  | 'brand'
  | 'accent'
  | 'success'
  | 'warning'
  | 'delayed'
  | 'danger'
  | 'critical'
  | 'blocked'
  | 'info'
  | 'processing'
  | 'ai'
  | 'attention';

export const tones: Tone[] = [
  'neutral',
  'brand',
  'accent',
  'success',
  'warning',
  'delayed',
  'danger',
  'critical',
  'blocked',
  'info',
  'processing',
  'ai',
  'attention',
];

/*
 * Solid (filled) tone classes. The fill is the family's `-fg` step — deep in light, pale in dark — and the
 * label is `text-fg-inverse`, which flips with it; tokens.test.ts holds that pair above AA in both themes.
 */
export const toneSolid: Record<Tone, string> = {
  neutral: 'bg-neutral-fg text-fg-inverse',
  brand: 'bg-primary text-primary-fg',
  accent: 'bg-accent-solid text-accent-on',
  success: 'bg-success-fg text-fg-inverse',
  warning: 'bg-warning-fg text-fg-inverse',
  delayed: 'bg-delayed-fg text-fg-inverse',
  danger: 'bg-danger-fg text-fg-inverse',
  critical: 'bg-danger-fg text-fg-inverse',
  blocked: 'bg-blocked-fg text-fg-inverse',
  info: 'bg-info-fg text-fg-inverse',
  processing: 'bg-processing-fg text-fg-inverse',
  ai: 'bg-ai-fg text-fg-inverse',
  attention: 'bg-attention-fg text-fg-inverse',
};

/**
 * Subtle chip fill: the `-subtle` background with its matching `-fg` text and **no border**.
 * This is the default treatment for badges, status chips and tags.
 */
export const toneSubtle: Record<Tone, string> = {
  neutral: 'bg-neutral-subtle text-neutral-fg',
  brand: 'bg-primary-subtle text-primary-subtle-fg',
  accent: 'bg-accent-subtle text-accent-fg',
  success: 'bg-success-subtle text-success-fg',
  warning: 'bg-warning-subtle text-warning-fg',
  delayed: 'bg-delayed-subtle text-delayed-fg',
  danger: 'bg-danger-subtle text-danger-fg',
  critical: 'bg-danger-subtle text-danger-fg',
  blocked: 'bg-blocked-subtle text-blocked-fg',
  info: 'bg-info-subtle text-info-fg',
  processing: 'bg-processing-subtle text-processing-fg',
  ai: 'bg-ai-subtle text-ai-fg',
  attention: 'bg-attention-subtle text-attention-fg',
};

/** Soft (tinted) tone classes — the historical name for the subtle fill above. */
export const toneSoft: Record<Tone, string> = toneSubtle;

/** Outline tone classes: a hairline with no fill, for chips that must sit on a coloured surface. */
export const toneOutline: Record<Tone, string> = {
  neutral: 'border border-border-strong bg-transparent text-fg-muted',
  brand: 'border border-border-strong bg-transparent text-primary-subtle-fg',
  accent: 'border border-accent-border bg-transparent text-accent-fg',
  success: 'border border-success-border bg-transparent text-success-fg',
  warning: 'border border-warning-border bg-transparent text-warning-fg',
  delayed: 'border border-delayed-border bg-transparent text-delayed-fg',
  danger: 'border border-danger-border bg-transparent text-danger-fg',
  critical: 'border border-danger-border bg-transparent text-danger-fg',
  blocked: 'border border-blocked-border bg-transparent text-blocked-fg',
  info: 'border border-info-border bg-transparent text-info-fg',
  processing: 'border border-processing-border bg-transparent text-processing-fg',
  ai: 'border border-ai-border bg-transparent text-ai-fg',
  attention: 'border border-attention-border bg-transparent text-attention-fg',
};

/** Indicator colour (dots, progress fills, severity bars). Meets 3:1 on every surface. */
export const toneAccent: Record<Tone, string> = {
  neutral: 'bg-neutral',
  brand: 'bg-primary',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  delayed: 'bg-delayed',
  danger: 'bg-danger',
  critical: 'bg-danger',
  blocked: 'bg-blocked',
  info: 'bg-info',
  processing: 'bg-processing',
  ai: 'bg-ai',
  attention: 'bg-attention',
};

/** Foreground colour of the tone (icons, emphasised text). Uses the AA `-fg` step. */
export const toneText: Record<Tone, string> = {
  neutral: 'text-fg-muted',
  brand: 'text-primary-subtle-fg',
  accent: 'text-accent-fg',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  delayed: 'text-delayed-fg',
  danger: 'text-danger-fg',
  critical: 'text-danger-fg',
  blocked: 'text-blocked-fg',
  info: 'text-info-fg',
  processing: 'text-processing-fg',
  ai: 'text-ai-fg',
  attention: 'text-attention-fg',
};

/**
 * Tinted panel classes (callouts). Colour only — the component decides which edge carries the hairline.
 * Body text stays `text-fg`; only the icon and title take the tone.
 */
export const tonePanel: Record<Tone, string> = {
  neutral: 'bg-neutral-bg text-fg border-neutral-border',
  brand: 'bg-primary-subtle text-fg border-border-strong',
  accent: 'bg-accent-subtle text-fg border-accent-border',
  success: 'bg-success-bg text-fg border-success-border',
  warning: 'bg-warning-bg text-fg border-warning-border',
  delayed: 'bg-delayed-bg text-fg border-delayed-border',
  danger: 'bg-danger-bg text-fg border-danger-border',
  critical: 'bg-danger-bg text-fg border-danger-border',
  blocked: 'bg-blocked-bg text-fg border-blocked-border',
  info: 'bg-info-bg text-fg border-info-border',
  processing: 'bg-processing-bg text-fg border-processing-border',
  ai: 'bg-ai-bg text-fg border-ai-border',
  attention: 'bg-attention-bg text-fg border-attention-border',
};
